import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Users,
  Building2,
  BookUser,
  CreditCard,
  Package,
  Server,
  Receipt,
  ScrollText,
  Settings,
  Settings2,
  Bell,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Mail,
  ShoppingBag,
  DollarSign,
  Key,
  Layers,
  Percent,
  Sliders,
  Ticket,
  Globe,
  Tags,
  AlertTriangle,
  BarChart3,
  TrendingUp,
  QrCode,
  FileClock,
  Gift,
  Disc3,
  CalendarClock,
  ArrowLeftRight,
  UserPlus,
  Wallet,
  Webhook,
  RotateCcw,
  FileChartColumn,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useState } from "react";
import { useAdminAuthStore } from "@/stores/adminAuthStore";

/** A sub-entry of a group: a page link with no icon of its own. */
interface NavSubItem {
  name: string;
  href: string;
  /**
   * Roles allowed to see this entry. Must match what the route and the endpoint accept — a
   * narrower list here hides a page the user can still reach by typing its URL.
   */
  requiredRole?: string | string[];
}

interface NavItem extends NavSubItem {
  icon: React.ComponentType<{ className?: string }>;
}

/**
 * An entry that opens a list of sub-entries. It is not a page and leads nowhere itself: clicking
 * it only shows or hides its list, which starts open while one of the sub-entries is the page shown.
 */
interface NavGroup {
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  children: NavSubItem[];
}

interface NavSection {
  label?: string;
  items: (NavItem | NavGroup)[];
}

/**
 * Sidebar sections, ordered by how often an operator needs them: the daily
 * customer and money work first, then the things that are set up once
 * (catalog, reports) and rarely (access, configuration).
 *
 * Section labels are mirrored by Breadcrumbs — keep both in step.
 */
const navigation: NavSection[] = [
  {
    items: [{ name: "Dashboard", href: "/admin", icon: LayoutDashboard }],
  },
  {
    label: "Customers",
    items: [
      { name: "Users", href: "/admin/users", icon: Users },
      { name: "Tenants", href: "/admin/tenants", icon: Building2 },
    ],
  },
  {
    // Ordered along the revenue lifecycle: order → payment → cycle → invoice.
    label: "Billing",
    items: [
      { name: "Orders", href: "/admin/orders", icon: ShoppingBag },
      { name: "Payments", href: "/admin/payments", icon: CreditCard },
      {
        name: "Billing Cycles",
        href: "/admin/billing-cycles",
        icon: CalendarClock,
      },
      { name: "E-Invoices", href: "/admin/e-invoices", icon: Receipt },
      { name: "Billing Info", href: "/admin/billing-info", icon: BookUser },
      { name: "Account Balances", href: "/admin/balances", icon: Wallet },
    ],
  },
  {
    label: "Infrastructure",
    items: [
      // Flat on purpose: the three resource pages sit side by side, each with its own icon. They
      // were one "Resources" group (NavGroup, still supported below) with "Resource List" as its
      // first sub-entry; to bring it back, replace these three entries with
      //   { name: "Resources", icon: Server, children: [{ name: "Resource List", href: … }, …] }
      // and drop the icons from the sub-entries.
      { name: "Resources", href: "/admin/resources", icon: Server },
      {
        name: "VM Reinstall Log",
        href: "/admin/resource-reinstalls",
        icon: RotateCcw,
        requiredRole: ["SuperAdmin", "SupportAdmin"],
      },
      {
        name: "VM Reinstall Report",
        href: "/admin/resource-reinstalls/report",
        icon: FileChartColumn,
        requiredRole: ["SuperAdmin", "SupportAdmin"],
      },
      { name: "Domains", href: "/admin/domains", icon: Globe },
      {
        name: "Domain Operations",
        href: "/admin/domains/operations",
        icon: AlertTriangle,
      },
    ],
  },
  {
    label: "Catalog",
    items: [
      { name: "Products", href: "/admin/products", icon: Package },
      { name: "Product Lines", href: "/admin/product-lines", icon: Layers },
      { name: "OS Images", href: "/admin/os-images", icon: Disc3 },
      {
        name: "Pricing Components",
        href: "/admin/pricing/components",
        icon: DollarSign,
      },
      {
        name: "Component Prices",
        href: "/admin/pricing/prices",
        icon: Sliders,
      },
      {
        name: "Billing Discounts",
        href: "/admin/billing-discounts",
        icon: Percent,
      },
      { name: "TLD Catalog", href: "/admin/tld-catalog", icon: Tags },
    ],
  },
  {
    label: "Reports",
    items: [
      {
        name: "Growth Report",
        href: "/admin/reports/growth",
        icon: TrendingUp,
        requiredRole: "SuperAdmin" as const,
      },
      {
        name: "Domain Reports",
        href: "/admin/domain-reports",
        icon: BarChart3,
        requiredRole: "SuperAdmin" as const,
      },
      {
        name: "WiService Status",
        href: "/admin/reports/wiservice",
        icon: Webhook,
        requiredRole: ["SuperAdmin", "SupportAdmin"] as const,
      },
    ],
  },
  {
    // Everything about the QR gateway in one place: how it is set up, what was exchanged with
    // it, and the sandbox tool that stands in for it.
    label: "T-PayGate",
    items: [
      {
        name: "Settings",
        href: "/admin/tpaygate",
        icon: QrCode,
        requiredRole: "SuperAdmin" as const,
      },
      {
        name: "Webhook Log",
        href: "/admin/tpaygate/webhook-log",
        icon: FileClock,
        requiredRole: ["SuperAdmin", "BillingAdmin"] as const,
      },
      {
        name: "Simulator",
        href: "/admin/tpaygate/webhook-simulator",
        icon: Webhook,
        requiredRole: "SuperAdmin" as const,
      },
    ],
  },
  {
    label: "Access & Security",
    items: [
      {
        name: "Admin Invitations",
        href: "/admin/invites",
        icon: UserPlus,
        requiredRole: "SuperAdmin" as const,
      },
      {
        name: "Invitation Codes",
        href: "/admin/invitation-codes",
        icon: Ticket,
        requiredRole: "SuperAdmin" as const,
      },
      { name: "API Keys", href: "/admin/api-keys", icon: Key },
      { name: "Audit Logs", href: "/admin/audit", icon: ScrollText },
      {
        name: "Notification Log",
        href: "/admin/notification-logs",
        icon: Bell,
      },
    ],
  },
  {
    label: "Configuration",
    items: [
      {
        name: "Platform Settings",
        href: "/admin/platform-settings",
        icon: Settings,
        requiredRole: "SuperAdmin" as const,
      },
      {
        name: "Registrar Settings",
        href: "/admin/registrar-settings",
        icon: Settings2,
      },
      {
        name: "Exchange Rates",
        href: "/admin/settings/exchange-rates",
        icon: ArrowLeftRight,
        requiredRole: "SuperAdmin" as const,
      },
      {
        name: "Sign-up Bonus",
        href: "/admin/signup-bonus",
        icon: Gift,
        requiredRole: "SuperAdmin" as const,
      },
      {
        name: "Email Templates",
        href: "/admin/email-templates",
        icon: Mail,
        requiredRole: "SuperAdmin" as const,
      },
      {
        // The one-off maintenance tools, one card each on the page. SuperAdmin only, like the
        // route and the endpoints behind it. Its "Update resource fields" card was the entry
        // "Data Updates" under Infrastructure until 2026-10-13.
        name: "Helper",
        href: "/admin/helper",
        icon: Wrench,
        requiredRole: "SuperAdmin" as const,
      },
    ],
  },
];

export function AdminSidebar() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const { user } = useAdminAuthStore();

  // Filter navigation items based on user role
  const visibleNavigation = navigation
    .map((section) => ({
      ...section,
      items: visibleEntries(section.items, user?.roles),
    }))
    .filter((section) => section.items.length > 0);

  // Longest-prefix match wins: a nested route with its own nav entry
  // (e.g. /admin/resource-reinstalls/report) must not also activate its parent
  // (/admin/resource-reinstalls). Detail routes (e.g. /admin/resources/:id) still activate
  // their parent list entry because no more-specific nav entry matches them.
  const allNavItems = visibleNavigation.flatMap((s) =>
    s.items.flatMap((entry) => (isNavGroup(entry) ? entry.children : [entry])),
  );
  const activeHref = allNavItems
    .filter(
      (it) =>
        location.pathname === it.href ||
        location.pathname === it.href + "/" ||
        location.pathname.startsWith(it.href + "/"),
    )
    .sort((a, b) => b.href.length - a.href.length)[0]?.href;

  return (
    <aside
      className={cn(
        "flex flex-col border-r bg-card transition-all duration-300",
        collapsed ? "w-16" : "w-64",
      )}
    >
      {/* Logo */}
      <div className="flex h-12 items-center justify-between border-b px-3">
        {!collapsed && (
          <h1 className="text-xl font-bold text-primary">WiServices Admin</h1>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCollapsed(!collapsed)}
          className="ml-auto"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-2 py-4">
        <nav className="space-y-4">
          {visibleNavigation.map((section, sectionIndex) => (
            <div key={sectionIndex}>
              {!collapsed && section.label && (
                <p className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-muted-2">
                  {section.label}
                </p>
              )}
              <div className="space-y-1">
                {section.items.map((entry) =>
                  isNavGroup(entry) ? (
                    <NavGroupEntry
                      key={entry.name}
                      group={entry}
                      activeHref={activeHref}
                      collapsed={collapsed}
                      onExpandSidebar={() => setCollapsed(false)}
                    />
                  ) : (
                    <NavLink
                      key={entry.name}
                      item={entry}
                      isActive={entry.href === activeHref}
                      collapsed={collapsed}
                    />
                  ),
                )}
              </div>
            </div>
          ))}
        </nav>
      </ScrollArea>
    </aside>
  );
}

const NAV_ROW =
  "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors";
const NAV_ROW_ACTIVE = "bg-primary text-primary-foreground";
const NAV_ROW_IDLE = "text-ink-2 hover:bg-accent hover:text-accent-foreground";

function isNavGroup(entry: NavItem | NavGroup): entry is NavGroup {
  return "children" in entry;
}

function isAllowed(item: NavSubItem, roles: readonly string[] | undefined) {
  if (!item.requiredRole) return true;
  const allowed = Array.isArray(item.requiredRole)
    ? item.requiredRole
    : [item.requiredRole];
  return allowed.some((role) => roles?.includes(role));
}

/** The entries this user may see. A group keeps only its visible sub-entries and goes when none is left. */
function visibleEntries(
  entries: (NavItem | NavGroup)[],
  roles: readonly string[] | undefined,
) {
  const visible: (NavItem | NavGroup)[] = [];
  for (const entry of entries) {
    if (!isNavGroup(entry)) {
      if (isAllowed(entry, roles)) visible.push(entry);
      continue;
    }

    const children = entry.children.filter((child) => isAllowed(child, roles));
    if (children.length > 0) visible.push({ ...entry, children });
  }
  return visible;
}

function NavLink({
  item,
  isActive,
  collapsed,
}: {
  item: NavItem;
  isActive: boolean;
  collapsed: boolean;
}) {
  return (
    <Link
      to={item.href}
      className={cn(NAV_ROW, isActive ? NAV_ROW_ACTIVE : NAV_ROW_IDLE)}
    >
      <item.icon className="h-5 w-5 shrink-0" />
      {!collapsed && <span>{item.name}</span>}
    </Link>
  );
}

function NavGroupEntry({
  group,
  activeHref,
  collapsed,
  onExpandSidebar,
}: {
  group: NavGroup;
  activeHref: string | undefined;
  collapsed: boolean;
  onExpandSidebar: () => void;
}) {
  // Null until the operator clicks the group; until then the list follows the page shown.
  const [toggledOpen, setToggledOpen] = useState<boolean | null>(null);
  const hasActiveChild = group.children.some(
    (child) => child.href === activeHref,
  );
  const isOpen = toggledOpen ?? hasActiveChild;

  // The collapsed sidebar has no room for the sub-entries, so a click there widens it and opens
  // the list, and the group's icon carries the active colour; expanded, the sub-entry carries it.
  const handleToggle = () => {
    if (collapsed) {
      onExpandSidebar();
      setToggledOpen(true);
      return;
    }
    setToggledOpen(!isOpen);
  };
  const showsActive = hasActiveChild && collapsed;

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={isOpen && !collapsed}
        aria-label={collapsed ? group.name : undefined}
        className={cn(
          NAV_ROW,
          "w-full cursor-pointer text-left",
          showsActive ? NAV_ROW_ACTIVE : NAV_ROW_IDLE,
        )}
      >
        <group.icon className="h-5 w-5 shrink-0" />
        {!collapsed && <span className="flex-1">{group.name}</span>}
        {!collapsed && isOpen && <ChevronDown className="h-4 w-4 shrink-0" />}
        {!collapsed && !isOpen && <ChevronRight className="h-4 w-4 shrink-0" />}
      </button>
      {isOpen && !collapsed && (
        <div className="ml-4 space-y-1">
          {group.children.map((child) => (
            <Link
              key={child.name}
              to={child.href}
              className={cn(
                NAV_ROW,
                child.href === activeHref ? NAV_ROW_ACTIVE : NAV_ROW_IDLE,
              )}
            >
              {child.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
